/**
 * Funnel-platform analytics envelope per the plan §8.2.
 *
 * One Zod schema, used in three places:
 * - SPA emitter (`apps/funnel/src/funnel/hooks/useFunnelAnalytics.ts`) —
 *   builds the envelope and POSTs it to `/api/events`.
 * - `apps/analytics-api` `POST /api/events` — validates the inbound
 *   envelope before writing to the Cloudflare Analytics Engine dataset.
 * - CI / unit tests — every test that emits an event uses the same
 *   schema so a renamed field fails the build everywhere.
 *
 * The Zod schema doubles as the type contract: `FunnelEventEnvelope` is
 * derived from the schema so the SPA cannot drift from the API contract.
 */

import { z } from "zod";

import { fnv1a32 } from "./fnv";

/** Client-originated funnel events from plan §8.1. */
const CLIENT_EVENT_KINDS = [
  "funnel_landing_viewed",
  "funnel_started",
  "funnel_step_viewed",
  "funnel_answer_selected",
  "funnel_step_completed",
  "funnel_back_clicked",
  "funnel_email_submitted",
  "funnel_signup_completed",
  "funnel_handoff_clicked",
  "funnel_loading_viewed",
  "funnel_character_revealed",
  "funnel_first_message_previewed",
  "paywall_viewed",
  "plan_selected",
  "purchase_completed",
  "funnel_abandoned",
] as const;

/** All server-originated event kinds (NX-10025). */
export const CAPI_EVENT_KINDS = [
  "capi_account_created",
  "capi_subscription_started",
  "capi_subscription_renewed",
  "capi_subscription_upgraded",
  "capi_subscription_cancelled",
  "capi_purchase_completed",
  "capi_refund_completed",
] as const;

/** All known event names — derived from client + CAPI lists to prevent drift. */
export const FUNNEL_EVENT_NAMES = [...CLIENT_EVENT_KINDS, ...CAPI_EVENT_KINDS] as const;

/** Events that carry positive amount_usd in double2 (NX-10025). */
export const REVENUE_EVENT_KINDS = [
  "capi_subscription_started",
  "capi_subscription_renewed",
  "capi_subscription_upgraded",
  "capi_purchase_completed",
] as const;

/** Events that carry negative amount_usd (NX-10050). */
export const REFUND_EVENT_KINDS = ["capi_refund_completed"] as const;

/** Events that count toward complete_reg_rate (NX-10025). */
export const SIGNUP_EVENT_KINDS = ["funnel_signup_completed", "capi_account_created"] as const;

/** Events that count toward paid_conv_rate (NX-10025). */
export const PAID_CONVERSION_EVENT_KINDS = ["capi_subscription_started"] as const;

/* ── Pre-built SQL IN-clause filters (shared across rollup SQL builders) ── */
const sqlIn = (kinds: readonly string[]): string => kinds.map((k) => `'${k}'`).join(", ");

export const REVENUE_KIND_SQL = sqlIn(REVENUE_EVENT_KINDS);
export const REFUND_KIND_SQL = sqlIn(REFUND_EVENT_KINDS);
/** All monetary event kinds (revenue + refunds) for SUM(amount_usd) aggregation (NX-10050). */
export const MONETARY_KIND_SQL = sqlIn([...REVENUE_EVENT_KINDS, ...REFUND_EVENT_KINDS]);
export const SIGNUP_KIND_SQL = sqlIn(SIGNUP_EVENT_KINDS);
export const PAID_CONV_KIND_SQL = sqlIn(PAID_CONVERSION_EVENT_KINDS);

export const funnelEventNameSchema = z.enum(FUNNEL_EVENT_NAMES);
export type FunnelEventName = z.infer<typeof funnelEventNameSchema>;

const attributionSchema = z.object({
  utm_source: z.string().optional(),
  utm_medium: z.string().optional(),
  utm_campaign: z.string().optional(),
  utm_content: z.string().optional(),
  utm_term: z.string().optional(),
  fbclid: z.string().optional(),
  fbc: z.string().optional(),
  fbp: z.string().optional(),
  campaign_id: z.string().optional(),
  inlet_id: z.string().optional(),
  adset_id: z.string().optional(),
  ad_id: z.string().optional(),
  placement: z.string().optional(),
  site_source_name: z.string().optional(),
  account_id: z.string().optional(),
  network: z.string().optional(),
  click_id: z.string().optional(),
  first_touch_at: z.string().optional(),
  last_touch_at: z.string().optional(),
  affiliate_id: z.string().optional(),
  tap_vid: z.string().optional(),
  crak_revenue_click_id: z.string().optional(),
  partner: z.string().optional(),
});

export const deviceSchema = z.object({
  type: z.enum(["mobile", "desktop", "tablet"]),
  os: z.string().optional(),
  browser: z.string().optional(),
});

export const funnelEventEnvelopeSchema = z
  .object({
    /** Event discriminator; one of the 15 plan §8.1 names. */
    event: funnelEventNameSchema,
    /** ISO 8601 timestamp at emit time (client clock). */
    timestamp: z.string().datetime(),
    /** Dedup key derived from session_id + event + step_id + timestamp (PLAN.md §6.4). */
    event_id: z.string().min(1),
    /** Sticky anonymous id for variant bucketing + funnel correlation. */
    anonymous_id: z.string().min(1),
    /** Authenticated user id, when present. */
    user_id: z.string().min(1).optional(),
    /** Per-funnel session id (matches `sessions.id` in D1). */
    session_id: z.string().min(1),
    /** Application + funnel + variant ids the event belongs to. */
    application_id: z.string().min(1),
    funnel_id: z.string().min(1),
    override_id: z.string().min(1).optional(),
    /** Step provenance; absent for ambient events (`funnel_landing_viewed`, etc.). */
    step_id: z.string().min(1).optional(),
    step_index: z.number().int().nonnegative().optional(),
    /** Running experiment id when the session was assigned via experiment bucketing (NX-9773). */
    experiment_id: z.string().min(1).optional(),
    /** NX-9949: Experiment arm id assigned via bucketing. */
    arm_id: z.string().min(1).optional(),
    /** NX-9847: Published funnel version number. Omitted when serving from draft tables. */
    funnel_version: z.number().int().positive().optional(),
    /** NX-9941: Admin preview flag. When true, events route skips WAE write. */
    is_preview: z.boolean().optional(),
    /** Plan §8.2 attribution payload. Optional for late events that lost it. */
    attribution: attributionSchema.optional(),
    /** Device classification — drives §8.3 segmentation. */
    device: deviceSchema.optional(),
    /** Revenue amount in USD for CAPI events; negative for refunds (NX-10050). */
    amount_usd: z.number().optional(),
  })
  // Allow per-event extra fields (e.g. `step_id` on step_viewed,
  // `plan_id` on plan_selected) without bloating the schema with one
  // refinement per event. Strict-mode would reject those harmlessly.
  .passthrough();

export type FunnelEventEnvelope = z.infer<typeof funnelEventEnvelopeSchema>;

/**
 * Helper for building an envelope that satisfies the schema. Used by the
 * SPA hook and by tests.
 */
export const buildEventId = (
  sessionId: string,
  event: string,
  stepId: string | undefined,
  timestamp: string,
): string => {
  const seed = `${sessionId}:${event}:${stepId ?? ""}:${timestamp}`;
  return `evt_${fnv1a32(seed).toString(36)}_${sessionId}`;
};

export const buildFunnelEventEnvelope = (input: {
  event: FunnelEventName;
  applicationId: string;
  funnelId: string;
  overrideId?: string | null;
  sessionId: string;
  anonymousId: string;
  userId?: string;
  stepId?: string;
  stepIndex?: number;
  /** NX-9773: Experiment id when bucketed. */
  experimentId?: string;
  /** NX-9949: Experiment arm id when bucketed. */
  armId?: string;
  /** NX-9847: Published funnel version number. */
  funnelVersion?: number;
  attribution?: z.infer<typeof attributionSchema>;
  device?: z.infer<typeof deviceSchema>;
  /** Extra event-specific fields, merged into the envelope. */
  data?: Record<string, unknown>;
}): FunnelEventEnvelope => {
  const timestamp = new Date().toISOString();
  const eventId = buildEventId(input.sessionId, input.event, input.stepId, timestamp);
  return {
    ...input.data,
    event: input.event,
    timestamp,
    event_id: eventId,
    application_id: input.applicationId,
    funnel_id: input.funnelId,
    ...(input.overrideId ? { override_id: input.overrideId } : {}),
    session_id: input.sessionId,
    anonymous_id: input.anonymousId,
    ...(input.userId ? { user_id: input.userId } : {}),
    ...(input.stepId ? { step_id: input.stepId } : {}),
    ...(input.stepIndex !== undefined ? { step_index: input.stepIndex } : {}),
    ...(input.experimentId ? { experiment_id: input.experimentId } : {}),
    ...(input.armId ? { arm_id: input.armId } : {}),
    ...(input.funnelVersion !== undefined ? { funnel_version: input.funnelVersion } : {}),
    ...(input.attribution ? { attribution: input.attribution } : {}),
    ...(input.device ? { device: input.device } : {}),
  };
};
